'use client';

import { useEffect, useState } from 'react';

export interface InstanceFeatures {
  ekurs: boolean;
}

// Fail-safe privzetek: dokler strežnik ne potrdi, se e-KURS NE prikazuje.
const DEFAULT_FEATURES: InstanceFeatures = { ekurs: false };

let cached: InstanceFeatures | null = null;
let inflight: Promise<InstanceFeatures> | null = null;
const listeners = new Set<(f: InstanceFeatures) => void>();

async function fetchFeatures(): Promise<InstanceFeatures> {
  if (cached) return cached;
  if (!inflight) {
    inflight = fetch('/api/features', { cache: 'no-store' })
      .then((r) => (r.ok ? r.json() : DEFAULT_FEATURES))
      .then((f: InstanceFeatures) => {
        cached = { ...DEFAULT_FEATURES, ...f };
        return cached;
      })
      .catch(() => DEFAULT_FEATURES);
  }
  return inflight;
}

/**
 * Ponovno prebere flagе s strežnika in obvesti vse montirane useFeatures()
 * instance. Kliče /nadzor po preklopu e-KURS gumba — sicer bi ostali deli UI
 * v istem zavihku kazali staro stanje do reload-a.
 */
export async function refreshFeatures(): Promise<InstanceFeatures> {
  cached = null;
  inflight = null;
  const f = await fetchFeatures();
  listeners.forEach((l) => l(f));
  return f;
}

/** Feature flagi instance (en fetch na nalaganje strani, module-level cache). */
export function useFeatures(): InstanceFeatures {
  const [features, setFeatures] = useState<InstanceFeatures>(cached ?? DEFAULT_FEATURES);

  useEffect(() => {
    let alive = true;
    const listener = (f: InstanceFeatures) => {
      if (alive) setFeatures(f);
    };
    listeners.add(listener);
    fetchFeatures().then(listener);
    return () => {
      alive = false;
      listeners.delete(listener);
    };
  }, []);

  return features;
}
